<template>
  <div
    class="c-room-card c-room-card--large v-room-carousel"
    ref="RoomCard"
    :class="{
      'c-room-card--dms-debug-filtered': debugFilteredOut,
    }"
    :data-id="room.room_id"
    :data-test-venue-id="room.venue_id"
    @mouseenter="onMouseOver()"
    @mouseleave="onMouseOut()"
  >
    <div
      v-if="isFavouritesEnabled"
      class="c-room-card__heart"
      :class="{'c-room-card__heart--selected': isFavourite}"
      @click="toggleFavourite(room.room_id)"
    />

    <div class="js-room-card-img-wrap relative">
      <div
        v-if="showChristmasPackageBadge"
        class="absolute top-2 left-2 z-[2] bg-white border border-solid border-grey-400 rounded-full px-2 py-1 text-xxs leading-[14px] font-semibold text-grey-800 tracking-wide whitespace-nowrap pointer-events-none"
      >
        {{ christmasPackageBadgeLabel }}
      </div>
      <RoomNativeCarousel
        :room="room"
        :index="index"
        @carouselClicked="roomClick"
        @beforeSlide="onBeforeSlide"
      />
    </div>

    <a
      class="c-room-card__details"
      :href="room.room_url"
      target="_blank"
    >
      <div class="c-room-card__details_row" :class="{'c-room-card__details_row--align-start c-room-card__tags-grid': tags.length}">
        <div v-if="tags.length" class="flex flex-wrap gap-1 justify-start mb-0.5">
          <RoomCardAdBadge v-if="room.is_promoted" />
          <div v-for="tag in tags" class="bg-grey-200 text-xs px-1 py-0.5 rounded-md overflow-hidden whitespace-nowrap text-ellipsis" :key="tag">
            {{ tag }}
          </div>
        </div>
        <div v-else class="flex items-center gap-1 min-w-0">
          <RoomCardAdBadge v-if="room.is_promoted" />
          <div class="c-room-card__room_name">
            {{ room.room_name }}
          </div>
        </div>

        <div class="c-room-card__capacity" :class="{'mt-0.5':tags.length}">
          <div
            v-if="room.room_seating_capacity"
            class="c-room-card__capacity-item c-room-card__capacity-item--seating"
          >
            {{ room.room_seating_capacity }}
          </div>

          <div
            v-if="room.room_standing_capacity"
            class="c-room-card__capacity-item c-room-card__capacity-item--standing"
          >
            {{ room.room_standing_capacity }}
          </div>
        </div>
      </div>

      <div class="c-room-card__details_row c-room-card__details_row--no-margin">
        <div
          v-if="room.metro_station && !tags.length && searchEventCategory !== 'meeting'"
          class="c-room-card__location c-room-card__location--metro"
          :class="{
            'bg-left': responsePeriod,
          }"
        >
          {{ room.metro_station }}
        </div>
        <div
          v-else
          :class="{
            'c-room-card__venue_name font-semibold': tags.length,
            'text-start': !tags.length
          }"
        >
          {{ room.venue_name }}
        </div>

        <div
          v-if="room.room_reviews_count && room.room_rating"
          class="c-room-card__rating"
          :class="'c-room-card__rating--' + room.room_rating"
          :aria-label="`${room.room_name}'s rating: ${room.room_rating} stars (${room.room_reviews_count} reviews)`"
        >
          ({{ room.room_reviews_count }})
        </div>

        <template v-if="hasBottomLeftBadge && hasNoLocationNorRating">
          <div
            v-if="room.is_supervenue"
            class="c-room-card__supervenue"
          >
            <img
              alt="Supervenue"
              :src="'/build/images/supervenue.svg'"
            >
            #Supervenue
          </div>
        </template>
      </div>

      <div class="c-room-card__details_row">
        <div class="c-room-card__location__wrapper">

          <div
            v-if="room.metro_station && (tags.length || searchEventCategory === 'meeting')"
            class="c-room-card__location c-room-card__location--metro"
            :class="{
              'bg-left': responsePeriod,
            }"
          >
            {{ room.metro_station }}
          </div>
          <div
            v-if="room.geo_project_location"
            class="c-room-card__location"
            :class="{
              'bg-left': responsePeriod,
            }"
          >
            {{ room.geo_project_location }}
          </div>
        </div>

        <template v-if="hasBottomLeftBadge && !hasNoLocationNorRating">
          <div
            v-if="room.is_supervenue"
            class="c-room-card__supervenue"
          >
            <img
              alt="Supervenue"
              :src="'/build/images/supervenue.svg'"
            >
            #Supervenue
          </div>
        </template>
      </div>
      <div v-if="managerLastActivityOnline !== null && managerLastActivityOnline < 15" class="text-xs text-green flex justify-items-start mt-1">
        <ActiveTimeAgoLabel :value="managerLastActivityOnline" />
      </div>
      <div v-else-if="responsePeriod" class="text-xs text-grey-800 flex justify-items-start mt-1">
        <ClockIcon class="text-grey-700 mr-0.5 w-4 h-4 p-px" />
        <span class="pt-px">Responds within {{ responsePeriod }}</span>
      </div>
    </a>
    <div class="c-room-card__price c-room-card__price--htmlBottom">
      from <span class="c-room-card__price_val">{{ room.room_price }}</span><br>
      {{ room.room_price_method }} {{ room.room_price_type ? ' / ' + room.room_price_type : '' }}
    </div>
  </div>
</template>

<script>
import ClockIcon from '@tagvenue/icons/24/line/time/clock.svg';

import ActiveTimeAgoLabel from '@/components/Ui/ActiveTimeAgoLabel.vue';
import { generateUniqueId } from '@/services/helpers.js';
import { formatResponseTime } from '@/services/response-time-formatter';

import RoomCardAdBadge from './RoomCardAdBadge.vue';
import RoomNativeCarousel from './RoomNativeCarousel.vue';

// Christmas room tags: christmas-dinner (101), christmas-party (103), xmas-party (107),
// holiday-party (267). has_event_offer_packages is true for any offer type with packages,
// so the badge is still gated to these Christmas contexts; 101/103 mirror RoomTagEntity constants.
const CHRISTMAS_TAG_IDS = [101, 103, 107, 267];
const CHRISTMAS_TAG_SLUGS = ['christmas-party', 'christmas-dinner', 'xmas-party', 'holiday-party'];
// US/CA say "Holiday" instead of "Christmas"; the underlying tags/filters stay the same.
const HOLIDAY_LABEL_COUNTRY_CODES = ['US', 'CA'];

export default {
  name: 'RoomCard',
  emits: ['roomClick', 'mouseOver', 'mouseOut', 'beforeSlide', 'trackClick'],
  components: {
    RoomCardAdBadge,
    RoomNativeCarousel,
    ActiveTimeAgoLabel,
    ClockIcon,
  },
  props: {
    room: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isFavouritesEnabled: {
      type: Boolean,
      default: true,
    },
    managerLastActivityOnline: {
      type: Number,
      default: null,
      required: false,
    },
    roomNativeCarousel: {
      type: Boolean,
      default: false,
    },
    searchEventCategory: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      hasBottomLeftBadge: this.room.is_supervenue,
      isFavourite: null,
      uniqueId: generateUniqueId(),
      hasNoLocationNorRating: !this.room.geo_project_location && !this.room.metro_station
        && !(this.room.room_reviews_count && this.room.room_rating),
      photoNumber: 1,
    };
  },
  computed: {
    // The card also renders outside the search page, where the store knows of no search.
    searchTheClientIsBrowsing() {
      const { eventTypeSlug, filters } = this.$store?.state ?? {};
      if (!eventTypeSlug) {
        return {};
      }

      return {
        roomTagSlug: eventTypeSlug,
        searchedDate: filters?.date?.value,
      };
    },
    debugFilteredOut() {
      return this.$store.state.debugFilterMode
        && this.$store.state.debugFilterModeRooms
        && !this.$store.state.debugFilterModeRooms[this.room.room_id];
    },
    responsePeriod() {
      return formatResponseTime(this.room.response_time);
    },
    cateringOptions() {
      return this.room?.catering_options_ids || [];
    },
    tags() {
      if (this.searchEventCategory === 'meeting') {
        return [this.room.venue_type_label];
      }
      const tags = [];
      if (this.room.space_venue_type_label_variant) {
        tags.push(this.room.space_venue_type_label_variant);
      }
      if (this.searchEventCategory !== 'production' && this.room.venue_part_short_label) {
        tags.push(this.room.venue_part_short_label);
      }
      return tags;
    },
    showChristmasPackageBadge() {
      if (!this.room.has_event_offer_packages) {
        return false;
      }
      if (CHRISTMAS_TAG_SLUGS.includes(this.$store.state.eventTypeSlug)) {
        return true;
      }
      const selectedTagIds = (this.$store.state.filters?.roomTags?.value || []).map(Number);
      return CHRISTMAS_TAG_IDS.some(id => selectedTagIds.includes(id));
    },
    christmasPackageBadgeLabel() {
      return HOLIDAY_LABEL_COUNTRY_CODES.includes(this.$store.state.l10n?.ISOCountryCode)
        ? 'Holiday package'
        : 'Christmas package';
    },
  },
  methods: {
    roomClick() {
      App.es6.shared.sessionFlash.flash({
        position: +this.index + 1,
        page: +this.page,
        isFeatured: this.room.is_featured_room,
      }, `flash-room-${this.room.room_id}`);
    },
    onMouseOver() {
      this.$emit('mouseOver');
    },
    onMouseOut() {
      this.$emit('mouseOut');
    },
    toggleFavourite(id) {
      const self = this;
      App.es6.shared.wish_list.then(async (api) => {
        const saved = await api.toggleSaved(id, {}, self.searchTheClientIsBrowsing);

        if (saved) {
          self.isFavourite = !self.isFavourite;
        }
      });
    },
    trackRoomEvents() {
      App.es6.shared.track_click($(this.$refs.RoomCard), () => {
        const photoIndex = this.photoNumber;
        const photoUrl = this.room.photos[this.photoNumber - 1];

        if (!photoIndex || !photoUrl) {
          return;
        }
        this.$emit('trackClick', { photoIndex, photoUrl });
      });
    },
    onBeforeSlide({ arrowDirection, photoUrlAfter, photoIndex, photoUrl, currentPhotoNumber }) {
      this.$emit('beforeSlide', {
        roomId: this.room.room_id,
        arrowDirection,
        photoUrlAfter,
        photoIndex,
        photoUrl,
      });
      this.photoNumber = currentPhotoNumber;
    },
  },
  mounted() {
    window.App.es6.shared.wish_list.then(api => {
      this.isFavourite = api.isFavourite(+this.room.room_id);
      $.subscribe('wishList:added_from_url', (roomId) => {
        if (+this.room.room_id === roomId) {
          this.isFavourite = true;
        }
      });
    });
    this.trackRoomEvents();
  },
};
</script>

<style lang="scss">
@use 'sass:math';
@use "@sass/base/variables";

.c-room-card {
  width: 100%;
  margin-bottom: 16px;
  //position: relative;
}

.v-search-results-items  {
  padding: 0 16px;
  justify-content: space-between;
}

.v-search-results-items .c-room-card {
  width: calc(var(--width-1-2, 50%) - 26px); // 992-1680px

  @media #{variables.$for-large-and-up} { // 1680px+
    width: calc(var(--width-1-3, 33.333%) - 21px);
  }

  @media #{variables.$for-intermediate} { // 901-991px
    width: calc(var(--width-1-3, 33.333%) - 21px);
  }

  @media screen and (max-width: 900px) { // 480-900px
    width: calc(var(--width-1-2, 50%) - 26px);
  }

  @media #{variables.$for-mobile-small} { // 0-479px
    width: 100%;
  }
}

.c-room-card--dms-debug-filtered:not(:hover) {
  filter: grayscale(100%) opacity(50%) contrast(150%) brightness(200%);
}
</style>
